import React, { useEffect, useState, useMemo } from "react";
import PageBreadcrumb from "../../components/common/PageBreadCrumb";
import PageMeta from "../../components/common/PageMeta";
import AccordianTable, { ColumnDef, AccordionRow } from "../../components/custom/tables/AccordianTable";
import { ChevronDownIcon } from "../../icons";
import {
  fetchPolicies,
  fetchRuleSections,
  fetchDistrictOverlays,
  fetchDistrictCounties,
  PolicyRule,
  PolicySection,
  DistrictOverlay,
  CountyOverlay,
} from "../../lib/api/policies";

interface ProtectInterval {
  formation: string;
  top_ft: number;
  bottom_ft: number;
  tag_required?: boolean;
}

interface FormationTop {
  formation: string;
  top_ft: number;
  plug_required?: boolean;
  tag_required?: boolean;
}

interface FieldEntry {
  name: string;
  wbl?: { min_ft?: number; max_ft?: number };
  protect_intervals: ProtectInterval[];
  formation_tops: FormationTop[];
  field_remarks: string[];
  tag_formations: string[];
  squeeze?: { policy: string; formations: string[] };
  combine_formations?: { allow: boolean; groups: string[][] };
  enhanced_recovery_zone?: { behavior: { require_tag?: boolean; require_protect?: string[] } };
}

interface FormationDisplay {
  mode: 'flat' | 'named';
  fields: FieldEntry[];
}

function buildFormationDisplay(formationData: Record<string, unknown>): FormationDisplay {
  if (!formationData || Object.keys(formationData).length === 0) {
    return { mode: 'flat', fields: [] };
  }

  // Format A: flat formation_tops at top level
  const tops = formationData.formation_tops as FormationTop[] | undefined;
  if (Array.isArray(tops) && tops.length > 0) {
    return {
      mode: 'flat',
      fields: [{
        name: '',
        protect_intervals: [],
        formation_tops: tops,
        field_remarks: [],
        tag_formations: [],
      }],
    };
  }

  // Format B: named fields
  const rawFields = formationData.fields as Record<string, Record<string, unknown>> | undefined;
  if (rawFields && typeof rawFields === 'object') {
    const fields: FieldEntry[] = Object.entries(rawFields).map(([name, fd]) => ({
      name,
      wbl: fd.wbl as FieldEntry['wbl'],
      protect_intervals: (fd.protect_intervals as ProtectInterval[]) ?? [],
      formation_tops: (fd.formation_tops as FormationTop[]) ?? [],
      field_remarks: (fd.field_remarks as string[]) ?? [],
      tag_formations: ((fd.tag as { formations?: string[] })?.formations) ?? [],
      squeeze: fd.squeeze as FieldEntry['squeeze'],
      combine_formations: fd.combine_formations as FieldEntry['combine_formations'],
      enhanced_recovery_zone: fd.enhanced_recovery_zone as FieldEntry['enhanced_recovery_zone'],
    }));
    return { mode: 'named', fields };
  }

  return { mode: 'flat', fields: [] };
}

function formatRequirementValue(val: unknown): string {
  if (val === null || val === undefined) return "—";
  if (typeof val === "boolean") return val ? "Yes" : "No";
  if (typeof val === "number") return String(val);
  if (typeof val === "string") return val || "—";
  if (typeof val === "object" && "value" in (val as object)) {
    return formatRequirementValue((val as Record<string, unknown>).value);
  }
  return "—";
}

function humanizeKey(key: string): string {
  return key
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

interface CountyAccordionProps {
  counties: CountyOverlay[];
}

const CountyAccordion: React.FC<CountyAccordionProps> = ({ counties }) => {
  const [expanded, setExpanded] = useState<Record<number, boolean>>({});
  const toggle = (id: number) => setExpanded((prev) => ({ ...prev, [id]: !prev[id] }));

  return (
    <div className="-mx-4 -my-3 divide-y divide-gray-100 dark:divide-white/[0.05]">
      {counties.map((county) => {
        const display = buildFormationDisplay(county.formation_data);
        const totalFormations = display.fields.reduce((sum, f) => sum + f.formation_tops.length + f.protect_intervals.length, 0);
        const fieldCount = display.mode === 'named' ? display.fields.length : 0;
        const isOpen = Boolean(expanded[county.id]);

        return (
          <div key={county.id}>
            <button
              onClick={() => toggle(county.id)}
              className="w-full flex items-center gap-3 px-4 py-3 text-left transition-colors hover:bg-gray-100 dark:hover:bg-gray-800/60 cursor-pointer"
            >
              <ChevronDownIcon
                className={`w-4 h-4 text-gray-400 flex-shrink-0 transition-transform ${
                  isOpen ? "rotate-180" : ""
                }`}
              />
              <span className="font-medium text-gray-700 dark:text-gray-300">
                {county.county_name}
              </span>
              {display.mode === 'named' && fieldCount > 0 ? (
                <span className="ml-auto text-xs text-gray-400 dark:text-gray-500 flex-shrink-0">
                  {fieldCount} field{fieldCount !== 1 ? 's' : ''}
                </span>
              ) : totalFormations > 0 ? (
                <span className="ml-auto text-xs text-gray-400 dark:text-gray-500 flex-shrink-0">
                  {totalFormations} formation{totalFormations !== 1 ? 's' : ''}
                </span>
              ) : null}
            </button>

            {isOpen && (
              <div className="px-10 pb-4 space-y-4">
                {/* Notes */}
                {Array.isArray(county.notes) && county.notes.length > 0 && (
                  <div className="rounded-lg bg-blue-50 dark:bg-blue-500/10 px-4 py-3">
                    <p className="text-xs font-semibold text-blue-700 dark:text-blue-400 mb-2 uppercase tracking-wide">
                      Notes
                    </p>
                    <ul className="space-y-1">
                      {county.notes.map((note, i) => (
                        <li key={i} className="text-xs text-blue-800 dark:text-blue-300 leading-relaxed">
                          {note}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Requirements */}
                {Object.keys(county.requirements).length > 0 && (
                  <div>
                    <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">
                      Requirements
                    </p>
                    <dl className="grid grid-cols-1 gap-1 sm:grid-cols-2">
                      {Object.entries(county.requirements).map(([key, val]) => (
                        <div key={key} className="flex gap-2 text-xs">
                          <dt className="text-gray-500 dark:text-gray-400 shrink-0">
                            {humanizeKey(key)}:
                          </dt>
                          <dd className="font-medium text-gray-800 dark:text-gray-200">
                            {formatRequirementValue(val)}
                          </dd>
                        </div>
                      ))}
                    </dl>
                  </div>
                )}

                {/* Field / Formation Data */}
                {(() => {
                  if (display.fields.length === 0) return null;
                  return (
                    <div className="space-y-4">
                      {display.fields.map((field, fieldIdx) => (
                        <div
                          key={fieldIdx}
                          className={display.mode === 'named' ? 'rounded-lg border border-gray-200 dark:border-white/[0.08] overflow-hidden' : ''}
                        >
                          {/* Field header — only for named fields */}
                          {display.mode === 'named' && (
                            <div className="flex flex-wrap items-center gap-2 px-3 py-2 bg-gray-50 dark:bg-white/[0.03] border-b border-gray-200 dark:border-white/[0.08]">
                              <span className="font-semibold text-sm text-gray-800 dark:text-gray-200">
                                {field.name}
                              </span>
                              {field.wbl && (
                                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-700 dark:bg-blue-500/20 dark:text-blue-300">
                                  WBL: {field.wbl.min_ft ?? ''}
                                  {field.wbl.max_ft ? `–${field.wbl.max_ft}` : '+'} ft
                                </span>
                              )}
                              {field.enhanced_recovery_zone && (
                                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-700 dark:bg-orange-500/20 dark:text-orange-300">
                                  Enhanced Recovery Zone
                                </span>
                              )}
                              {field.tag_formations.length > 0 && (
                                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-700 dark:bg-yellow-500/20 dark:text-yellow-300">
                                  Tag: {field.tag_formations.join(', ')}
                                </span>
                              )}
                              {field.squeeze && (
                                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-700 dark:bg-purple-500/20 dark:text-purple-300">
                                  Squeeze ({field.squeeze.policy}): {field.squeeze.formations.join(', ')}
                                </span>
                              )}
                            </div>
                          )}

                          <div className={`space-y-3 ${display.mode === 'named' ? 'p-3' : ''}`}>
                            {/* Field Remarks */}
                            {field.field_remarks.length > 0 && (
                              <div className="rounded bg-amber-50 dark:bg-amber-500/10 px-3 py-2">
                                <p className="text-xs font-semibold text-amber-700 dark:text-amber-400 mb-1 uppercase tracking-wide">Field Remarks</p>
                                <ul className="space-y-0.5">
                                  {field.field_remarks.map((remark, i) => (
                                    <li key={i} className="text-xs text-amber-800 dark:text-amber-300">{remark}</li>
                                  ))}
                                </ul>
                              </div>
                            )}

                            {/* Combine Formations */}
                            {field.combine_formations?.allow && field.combine_formations.groups.length > 0 && (
                              <div>
                                <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">Combinable Formation Groups</p>
                                <ul className="space-y-0.5">
                                  {field.combine_formations.groups.map((group, gi) => (
                                    <li key={gi} className="text-xs text-gray-600 dark:text-gray-400">
                                      {group.join(' + ')}
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            )}

                            {/* Protect Intervals */}
                            {field.protect_intervals.length > 0 && (
                              <div>
                                <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">
                                  Protect Intervals (Water Board)
                                </p>
                                <table className="w-full text-xs border-collapse">
                                  <thead>
                                    <tr className="border-b border-gray-200 dark:border-white/[0.08]">
                                      <th className="py-1.5 pr-4 text-left font-medium text-gray-500 dark:text-gray-400">Formation</th>
                                      <th className="py-1.5 pr-4 text-left font-medium text-gray-500 dark:text-gray-400">Top (ft)</th>
                                      <th className="py-1.5 pr-4 text-left font-medium text-gray-500 dark:text-gray-400">Bottom (ft)</th>
                                      <th className="py-1.5 text-left font-medium text-gray-500 dark:text-gray-400">Tag Required</th>
                                    </tr>
                                  </thead>
                                  <tbody className="divide-y divide-gray-100 dark:divide-white/[0.04]">
                                    {field.protect_intervals.map((pi, i) => (
                                      <tr key={i} className="hover:bg-gray-50 dark:hover:bg-white/[0.02]">
                                        <td className="py-1.5 pr-4 font-medium text-gray-800 dark:text-gray-200">{pi.formation}</td>
                                        <td className="py-1.5 pr-4 text-gray-600 dark:text-gray-400">{pi.top_ft}</td>
                                        <td className="py-1.5 pr-4 text-gray-600 dark:text-gray-400">{pi.bottom_ft}</td>
                                        <td className="py-1.5">
                                          {pi.tag_required
                                            ? <span className="text-warning-600 dark:text-warning-400 font-medium">Yes</span>
                                            : <span className="text-gray-400">—</span>}
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            )}

                            {/* Formation Tops */}
                            {field.formation_tops.length > 0 && (
                              <div>
                                <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">
                                  Formation Tops
                                </p>
                                <table className="w-full text-xs border-collapse">
                                  <thead>
                                    <tr className="border-b border-gray-200 dark:border-white/[0.08]">
                                      <th className="py-1.5 pr-4 text-left font-medium text-gray-500 dark:text-gray-400">Formation</th>
                                      <th className="py-1.5 pr-4 text-left font-medium text-gray-500 dark:text-gray-400">Top (ft)</th>
                                      <th className="py-1.5 pr-4 text-left font-medium text-gray-500 dark:text-gray-400">Plug Required</th>
                                      <th className="py-1.5 text-left font-medium text-gray-500 dark:text-gray-400">Tag Required</th>
                                    </tr>
                                  </thead>
                                  <tbody className="divide-y divide-gray-100 dark:divide-white/[0.04]">
                                    {field.formation_tops.map((ft, i) => (
                                      <tr key={i} className="hover:bg-gray-50 dark:hover:bg-white/[0.02]">
                                        <td className="py-1.5 pr-4 font-medium text-gray-800 dark:text-gray-200">{ft.formation}</td>
                                        <td className="py-1.5 pr-4 text-gray-600 dark:text-gray-400">{ft.top_ft ?? '—'}</td>
                                        <td className="py-1.5 pr-4">
                                          {ft.plug_required
                                            ? <span className="text-success-600 dark:text-success-400 font-medium">Yes</span>
                                            : <span className="text-gray-400">—</span>}
                                        </td>
                                        <td className="py-1.5">
                                          {ft.tag_required
                                            ? <span className="text-warning-600 dark:text-warning-400 font-medium">Yes</span>
                                            : <span className="text-gray-400">—</span>}
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  );
                })()}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

// ── SectionAccordion helpers ──────────────────────────────────────────────────

/** Depth-0: single lowercase letter, e.g. "a", "b" */
const DEPTH0_RE = /^[a-z]$/;
/** Depth-1: letter + numeric paren, e.g. "a(1)", "b(12)" */
const DEPTH1_RE = /^[a-z]\(\d+\)$/;

interface Depth2Item {
  section: PolicySection;
}

interface Depth1Item {
  section: PolicySection;
  children: Depth2Item[];
}

interface SectionGroup {
  header: PolicySection;
  items: Depth1Item[];
}

/** Fallback group key for sections that appear before any depth-0 header */
const ORPHAN_KEY = "__orphan__";

interface OrphanGroup {
  header: null;
  items: Depth1Item[];
}

type AnyGroup = SectionGroup | OrphanGroup;

function buildSectionGroups(sections: PolicySection[]): AnyGroup[] {
  // Sort by order_idx so iteration is predictable
  const sorted = [...sections].sort((a, b) => a.order_idx - b.order_idx);

  const groups: AnyGroup[] = [];
  let currentGroup: AnyGroup | null = null;
  let currentDepth1: Depth1Item | null = null;

  for (const section of sorted) {
    if (DEPTH0_RE.test(section.path)) {
      // Start a new named group
      currentDepth1 = null;
      currentGroup = { header: section, items: [] };
      groups.push(currentGroup);
    } else if (DEPTH1_RE.test(section.path)) {
      // Ensure we have a group to attach to
      if (!currentGroup) {
        currentGroup = { header: null, items: [] };
        groups.push(currentGroup);
      }
      currentDepth1 = { section, children: [] };
      currentGroup.items.push(currentDepth1);
    } else {
      // Depth 2+ — attach to current depth-1 item if one exists
      if (!currentGroup) {
        currentGroup = { header: null, items: [] };
        groups.push(currentGroup);
      }
      if (!currentDepth1) {
        // No depth-1 parent yet — create a synthetic one to hold orphaned leaves
        currentDepth1 = { section, children: [] };
        currentGroup.items.push(currentDepth1);
      } else {
        currentDepth1.children.push({ section });
      }
    }
  }

  return groups;
}

// ── SectionAccordion component ────────────────────────────────────────────────

interface SectionAccordionProps {
  sections: PolicySection[];
}

const SectionAccordion: React.FC<SectionAccordionProps> = ({ sections }) => {
  const [expanded, setExpanded] = useState<Record<number, boolean>>({});
  const toggle = (id: number) =>
    setExpanded((prev) => ({ ...prev, [id]: !prev[id] }));

  // Check whether the data has the new 3-level path structure
  const hasHierarchy = sections.some(
    (s) => DEPTH0_RE.test(s.path) || DEPTH1_RE.test(s.path)
  );

  // ── Fallback: flat render (old data format) ───────────────────────────────
  if (!hasHierarchy) {
    return (
      <div className="-mx-4 -my-3 divide-y divide-gray-100 dark:divide-white/[0.05]">
        {sections.map((section) => (
          <div key={section.id}>
            <button
              onClick={() => toggle(section.id)}
              className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-gray-100 dark:hover:bg-gray-800/60 transition-colors"
            >
              <ChevronDownIcon
                className={`w-4 h-4 text-gray-400 flex-shrink-0 transition-transform ${
                  expanded[section.id] ? "rotate-180" : ""
                }`}
              />
              <span className="font-mono text-xs text-brand-600 dark:text-brand-400 min-w-[4rem] flex-shrink-0">
                {section.path}
              </span>
              {section.heading && (
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  {section.heading}
                </span>
              )}
            </button>
            {expanded[section.id] && (
              <div className="px-12 pb-3 text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
                {section.text}
              </div>
            )}
          </div>
        ))}
      </div>
    );
  }

  // ── Hierarchical render ───────────────────────────────────────────────────
  const groups = buildSectionGroups(sections);

  return (
    <div className="-mx-4 -my-3">
      {groups.map((group, groupIdx) => (
        <div key={group.header ? group.header.id : `${ORPHAN_KEY}-${groupIdx}`}>
          {/* Depth-0 header label */}
          {group.header && (
            <div className="px-4 pt-4 pb-1">
              <p className="text-xs font-semibold uppercase tracking-widest text-gray-400 dark:text-gray-500">
                <span className="font-mono mr-1.5">({group.header.path})</span>
                {group.header.heading || group.header.text}
              </p>
            </div>
          )}

          {/* Depth-1 expandable rows */}
          <div className="divide-y divide-gray-100 dark:divide-white/[0.05]">
            {group.items.map((item) => {
              const isOpen = Boolean(expanded[item.section.id]);
              const preview =
                item.section.text.length > 120
                  ? item.section.text.slice(0, 120).trimEnd() + "…"
                  : item.section.text;

              return (
                <div key={item.section.id}>
                  <button
                    onClick={() => toggle(item.section.id)}
                    className="w-full flex items-center gap-3 px-4 py-3 pl-8 text-left hover:bg-gray-50 dark:hover:bg-gray-800/40 transition-colors"
                  >
                    <ChevronDownIcon
                      className={`w-4 h-4 text-gray-400 flex-shrink-0 transition-transform ${
                        isOpen ? "rotate-180" : ""
                      }`}
                    />
                    <span className="font-mono text-xs text-brand-600 dark:text-brand-400 flex-shrink-0 min-w-[5rem]">
                      {item.section.path}
                    </span>
                    <span className="text-sm text-gray-500 dark:text-gray-400 truncate">
                      {item.section.heading || preview}
                    </span>
                  </button>

                  {isOpen && (
                    <div className="pl-16 pr-4 pb-4">
                      {item.children.length > 0 ? (
                        // Depth-2+ children as definition list
                        <dl className="space-y-3">
                          {item.children.map((child) => (
                            <div key={child.section.id} className="flex gap-3">
                              <dt className="font-mono text-xs text-brand-600 dark:text-brand-400 flex-shrink-0 pt-0.5 min-w-[6rem]">
                                {child.section.path}
                              </dt>
                              <dd className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
                                {child.section.heading && (
                                  <span className="font-medium text-gray-700 dark:text-gray-300 mr-1">
                                    {child.section.heading}
                                  </span>
                                )}
                                {child.section.text}
                              </dd>
                            </div>
                          ))}
                        </dl>
                      ) : (
                        // No children — show the depth-1 item's full text
                        <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
                          {item.section.text}
                        </p>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
};

export default function Policies() {
  const [policies, setPolicies] = useState<PolicyRule[]>([]);
  const [sections, setSections] = useState<{ [key: number]: PolicySection[] }>({});
  const [loadingSections, setLoadingSections] = useState<{ [key: number]: boolean }>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [jurisdictionFilter, setJurisdictionFilter] = useState<string>("All");
  const itemsPerPage = 25;

  // District Overlays state
  const [districtJurisdiction, setDistrictJurisdiction] = useState<"TX" | "NM">("TX");
  const [districts, setDistricts] = useState<DistrictOverlay[]>([]);
  const [loadingDistricts, setLoadingDistricts] = useState(false);
  const [districtError, setDistrictError] = useState<string | null>(null);
  const [districtCounties, setDistrictCounties] = useState<{ [districtCode: string]: CountyOverlay[] }>({});
  const [loadingCounties, setLoadingCounties] = useState<{ [districtCode: string]: boolean }>({});

  // Fetch policies on component mount
  useEffect(() => {
    const loadPolicies = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await fetchPolicies();
        setPolicies(response);
      } catch (err) {
        console.error("Error fetching policies:", err);
        setError(
          err instanceof Error
            ? err.message
            : "Failed to load policies. Please try again."
        );
      } finally {
        setLoading(false);
      }
    };

    loadPolicies();
  }, []);

  // Fetch district overlays whenever the jurisdiction filter changes
  useEffect(() => {
    const loadDistricts = async () => {
      try {
        setLoadingDistricts(true);
        setDistrictError(null);
        const response = await fetchDistrictOverlays(districtJurisdiction);
        setDistricts(response);
      } catch (err) {
        console.error("Error fetching district overlays:", err);
        setDistrictError(
          err instanceof Error
            ? err.message
            : "Failed to load district overlays. Please try again."
        );
      } finally {
        setLoadingDistricts(false);
      }
    };

    loadDistricts();
  }, [districtJurisdiction]);

  // Lazily fetch counties for a district when its row is expanded
  const fetchCountiesForDistrict = async (districtCode: string) => {
    if (districtCounties[districtCode] || loadingCounties[districtCode]) return;

    try {
      setLoadingCounties((prev) => ({ ...prev, [districtCode]: true }));
      const counties = await fetchDistrictCounties(districtCode);
      setDistrictCounties((prev) => ({ ...prev, [districtCode]: counties }));
    } catch (err) {
      console.error(`Error fetching counties for district ${districtCode}:`, err);
    } finally {
      setLoadingCounties((prev) => ({ ...prev, [districtCode]: false }));
    }
  };

  // Fetch sections for a specific policy when accordion is expanded
  const fetchSectionsForPolicy = async (policyId: number, ruleId: string) => {
    // If already loaded or loading, skip
    if (sections[policyId] || loadingSections[policyId]) {
      return;
    }

    try {
      setLoadingSections((prev) => ({ ...prev, [policyId]: true }));
      const policySections = await fetchRuleSections(ruleId);
      setSections((prev) => ({ ...prev, [policyId]: policySections }));
    } catch (err) {
      console.error(`Error fetching sections for policy ${ruleId}:`, err);
    } finally {
      setLoadingSections((prev) => ({ ...prev, [policyId]: false }));
    }
  };

  // Filter by jurisdiction
  const filteredByJurisdiction = useMemo(() => {
    if (jurisdictionFilter === "All") return policies;
    return policies.filter(p => p.jurisdiction === jurisdictionFilter);
  }, [policies, jurisdictionFilter]);

  // Calculate pagination
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredByJurisdiction.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredByJurisdiction.length / itemsPerPage);

  // Format date helper
  const formatDate = (dateString: string | null) => {
    if (!dateString) return "—";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  // District overlay table columns
  const districtParentColumns: ColumnDef[] = [
    { key: "districtCode", label: "District" },
    { key: "jurisdiction", label: "Jurisdiction" },
    { key: "sourceFile", label: "Source File" },
    { key: "importedAt", label: "Imported" },
  ];

  const districtChildColumns: ColumnDef[] = [
    { key: "content", label: "Counties" },
  ];

  const districtRows: AccordionRow[] = useMemo(() => {
    return districts.map((d) => {
      const counties = districtCounties[d.district_code] || [];
      const isCountyLoading = loadingCounties[d.district_code] || false;

      let contentNode: React.ReactNode;
      if (isCountyLoading) {
        contentNode = (
          <span className="flex items-center gap-2 text-gray-400 dark:text-gray-500 italic text-sm">
            <span className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-brand-500 border-r-transparent" />
            Loading counties…
          </span>
        );
      } else if (counties.length > 0) {
        const districtReqs = Object.entries(d.requirements);
        const districtPrefs = Object.entries(d.preferences);
        contentNode = (
          <div className="-mx-4 -my-3">
            {(districtReqs.length > 0 || districtPrefs.length > 0) && (
              <div className="px-4 pt-4 pb-3 border-b border-gray-100 dark:border-white/[0.05] space-y-3">
                {districtReqs.length > 0 && (
                  <div>
                    <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">
                      District Requirements
                    </p>
                    <dl className="grid grid-cols-1 gap-1 sm:grid-cols-2">
                      {districtReqs.map(([key, val]) => (
                        <div key={key} className="flex gap-2 text-xs">
                          <dt className="text-gray-500 dark:text-gray-400 shrink-0">{humanizeKey(key)}:</dt>
                          <dd className="font-medium text-gray-800 dark:text-gray-200">{formatRequirementValue(val)}</dd>
                        </div>
                      ))}
                    </dl>
                  </div>
                )}
                {districtPrefs.length > 0 && (
                  <div>
                    <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">
                      Preferences
                    </p>
                    <dl className="grid grid-cols-1 gap-1 sm:grid-cols-2">
                      {districtPrefs.map(([key, val]) => (
                        <div key={key} className="flex gap-2 text-xs">
                          <dt className="text-gray-500 dark:text-gray-400 shrink-0">{humanizeKey(key)}:</dt>
                          <dd className="font-medium text-gray-800 dark:text-gray-200">{formatRequirementValue(val)}</dd>
                        </div>
                      ))}
                    </dl>
                  </div>
                )}
              </div>
            )}
            <CountyAccordion counties={counties} />
          </div>
        );
      } else if (districtCounties[d.district_code] !== undefined) {
        contentNode = (
          <span className="text-gray-400 dark:text-gray-500 italic text-sm">
            No counties found
          </span>
        );
      } else {
        contentNode = (
          <span className="text-gray-400 dark:text-gray-500 italic text-sm">
            Expand to load counties…
          </span>
        );
      }

      return {
        id: d.district_code,
        parent: {
          districtCode: (
            <span className="inline-block px-2 py-1 rounded-full bg-brand-100 text-brand-700 dark:bg-brand-500/20 dark:text-brand-300 text-xs font-medium">
              {d.district_code}
            </span>
          ),
          jurisdiction: d.jurisdiction,
          sourceFile: d.source_file,
          importedAt: new Date(d.imported_at).toLocaleDateString("en-US", {
            year: "numeric",
            month: "short",
            day: "numeric",
          }),
        },
        children: [{ content: contentNode }],
      };
    });
  }, [districts, districtCounties, loadingCounties]);

  // Parent columns
  const parentColumns: ColumnDef[] = [
    { key: "ruleId", label: "Rule ID" },
    { key: "title", label: "Title" },
    { key: "citation", label: "Citation" },
    { key: "jurisdiction", label: "Jurisdiction" },
    { key: "topic", label: "Topic" },
    { key: "effectiveFrom", label: "Effective From" },
  ];

  // Child columns for sections
  const childColumns: ColumnDef[] = [
    { key: "sections", label: "Sections" },
  ];

  // Format data for AccordianTable
  const rows: AccordionRow[] = useMemo(() => {
    return currentItems.map((policy) => {
      const policySections = sections[policy.id];
      const isLoading = loadingSections[policy.id];

      let sectionsNode: React.ReactNode;
      if (isLoading) {
        sectionsNode = (
          <span className="flex items-center gap-2 text-gray-400 dark:text-gray-500 italic text-sm">
            <span className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-brand-500 border-r-transparent" />
            Loading sections…
          </span>
        );
      } else if (policySections && policySections.length > 0) {
        sectionsNode = <SectionAccordion sections={policySections} />;
      } else if (policySections) {
        sectionsNode = <span className="text-gray-400 dark:text-gray-500 italic text-sm">No sections available</span>;
      } else {
        sectionsNode = <span className="text-gray-400 dark:text-gray-500 italic text-sm">Expand to load sections…</span>;
      }

      return {
        id: policy.id,
        parent: {
          ruleId: policy.rule_id,
          title: policy.title || "—",
          citation: policy.citation,
          jurisdiction: (
            <span className="inline-block px-2 py-1 rounded-full bg-brand-100 text-brand-700 dark:bg-brand-500/20 dark:text-brand-300 text-xs font-medium">
              {policy.jurisdiction}
            </span>
          ),
          topic: policy.topic || "—",
          effectiveFrom: formatDate(policy.effective_from),
        },
        children: [{ sections: sectionsNode }],
      };
    });
  }, [currentItems, sections, loadingSections]);

  if (loading) {
    return (
      <>
        <PageBreadcrumb pageTitle="Policies" />
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-brand-500 border-r-transparent"></div>
            <p className="mt-3 text-gray-600 dark:text-gray-400">
              Loading policies...
            </p>
          </div>
        </div>
      </>
    );
  }

  if (error) {
    return (
      <>
        <PageBreadcrumb pageTitle="Policies" />
        <div className="rounded-2xl border border-gray-200 bg-white dark:border-white/[0.05] dark:bg-white/[0.03]">
          <div className="p-6">
            <div className="rounded-lg bg-error-50 p-4 dark:bg-error-500/15">
              <p className="text-sm font-medium text-error-700 dark:text-error-400">
                {error}
              </p>
            </div>
          </div>
        </div>
      </>
    );
  }

  if (policies.length === 0) {
    return (
      <>
        <PageBreadcrumb pageTitle="Policies" />
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <p className="text-gray-600 dark:text-gray-400">
              No policies found
            </p>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <PageMeta
        title="Policies | RegulAgent"
        description="View and manage regulatory policies"
      />

      <PageBreadcrumb pageTitle="Policies" />

      <div className="rounded-2xl border border-gray-200 bg-white dark:border-white/[0.05] dark:bg-white/[0.03]">
        <div className="px-6 py-4">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h3 className="text-lg font-semibold text-gray-800 dark:text-white/90">
                All Policies
              </h3>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                {jurisdictionFilter === "All"
                  ? `Total: ${policies.length} policies`
                  : `Showing ${filteredByJurisdiction.length} of ${policies.length} policies`
                }{totalPages > 1 ? ` | Page ${currentPage} of ${totalPages}` : ""}
              </p>
              <div className="flex gap-2 mt-3">
                {["All", "TX", "NM"].map((jur) => (
                  <button
                    key={jur}
                    onClick={() => { setJurisdictionFilter(jur); setCurrentPage(1); }}
                    className={`px-3 py-1.5 text-sm font-medium rounded-lg transition-colors ${
                      jurisdictionFilter === jur
                        ? "bg-brand-500 text-white"
                        : "bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-400 dark:hover:bg-gray-700"
                    }`}
                  >
                    {jur === "All" ? "All States" : jur}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>

        <AccordianTable
          parentColumns={parentColumns}
          childColumns={childColumns}
          rows={rows}
          expandedByDefault={false}
          onExpand={(rowId) => {
            const policy = policies.find((p) => p.id === rowId);
            if (policy) {
              fetchSectionsForPolicy(policy.id, policy.rule_id);
            }
          }}
        />

        {/* Pagination Controls */}
        {totalPages > 1 && (
          <div className="px-6 py-4 border-t border-gray-200 dark:border-white/[0.05]">
            <div className="flex items-center justify-between">
              {/* Previous Button */}
              <button
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 dark:border-gray-600 dark:text-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 dark:hover:bg-white/5 transition-colors"
              >
                Previous
              </button>

              {/* Page Numbers */}
              <div className="flex gap-1">
                {Array.from({ length: totalPages }, (_, idx) => idx + 1).map(
                  (page) => (
                    <button
                      key={page}
                      onClick={() => setCurrentPage(page)}
                      className={`w-10 h-10 rounded-lg font-medium transition-colors ${
                        currentPage === page
                          ? "bg-brand-500 text-white"
                          : "text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-white/5"
                      }`}
                    >
                      {page}
                    </button>
                  )
                )}
              </div>

              {/* Next Button */}
              <button
                onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
                className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 dark:border-gray-600 dark:text-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 dark:hover:bg-white/5 transition-colors"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {/* District Overlays Section */}
      <div className="mt-6 rounded-2xl border border-gray-200 bg-white dark:border-white/[0.05] dark:bg-white/[0.03]">
        <div className="px-6 py-4">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h3 className="text-lg font-semibold text-gray-800 dark:text-white/90">
                District Overlays
              </h3>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                {loadingDistricts
                  ? "Loading districts…"
                  : districtError
                  ? "Error loading districts"
                  : districts.length === 0
                  ? "No district overlays found"
                  : `${districts.length} district${districts.length !== 1 ? "s" : ""} for ${districtJurisdiction}`}
              </p>
              <div className="flex gap-2 mt-3">
                {(["TX", "NM"] as const).map((jur) => (
                  <button
                    key={jur}
                    onClick={() => setDistrictJurisdiction(jur)}
                    className={`px-3 py-1.5 text-sm font-medium rounded-lg transition-colors ${
                      districtJurisdiction === jur
                        ? "bg-brand-500 text-white"
                        : "bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-400 dark:hover:bg-gray-700"
                    }`}
                  >
                    {jur}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>

        {loadingDistricts && (
          <div className="flex items-center justify-center py-10">
            <div className="text-center">
              <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-brand-500 border-r-transparent"></div>
              <p className="mt-3 text-gray-600 dark:text-gray-400">Loading district overlays…</p>
            </div>
          </div>
        )}

        {!loadingDistricts && districtError && (
          <div className="px-6 pb-6">
            <div className="rounded-lg bg-error-50 p-4 dark:bg-error-500/15">
              <p className="text-sm font-medium text-error-700 dark:text-error-400">
                {districtError}
              </p>
            </div>
          </div>
        )}

        {!loadingDistricts && !districtError && districts.length === 0 && (
          <div className="flex items-center justify-center py-10">
            <p className="text-gray-500 dark:text-gray-400">
              No district overlays found for {districtJurisdiction}.
            </p>
          </div>
        )}

        {!loadingDistricts && !districtError && districts.length > 0 && (
          <AccordianTable
            parentColumns={districtParentColumns}
            childColumns={districtChildColumns}
            rows={districtRows}
            expandedByDefault={false}
            onExpand={(rowId) => {
              fetchCountiesForDistrict(String(rowId));
            }}
          />
        )}
      </div>
    </>
  );
}
